import { entries, template, TemplateExecutor } from "@buurman/sdk";
import en from "../lang/en.json";

export type Locale = keyof typeof languages;
export type RawLanguage = typeof en;
export type TemplatifiedLanguage = {
    [P in keyof RawLanguage]: TemplateExecutor;
} & { [name: string]: TemplateExecutor };

const templatifyLanguage = (language: RawLanguage): TemplatifiedLanguage =>
    entries(language).reduce((words, [key, value]) => {
        try {
            const tpl = template(value, { imports: { i18n } });
            return { ...words, [key]: tpl };
        } catch (e) {
            console.error(`Invalid template$`);
            console.error(`  current locale: ${currentLocale}`);
            console.error(`  word          : ${key}`);
            return words;
        }
    }, {} as TemplatifiedLanguage);

export const supportsLocale = (locale: Locale) => locale in languages;

export const setLocale = (locale: Locale) => {
    if (!supportsLocale(locale)) {
        console.warn(`Unable to set locale to ${locale}`);
        return;
    }

    currentLocale = locale;
    currentWords = languages[currentLocale];
};

export const addWord = (
    word: string,
    templates: { [locale: string]: string },
) => {
    entries(templates).forEach(([locale, templateString]) => {
        if (!supportsLocale(locale as Locale)) {
            console.warn(`Can't add word to ${locale}, locale doesn't exist.`);
            return;
        }

        languages[locale as Locale][word] = template(templateString);
    });
};

export const i18n = <TData extends object = object>(
    word: keyof TemplatifiedLanguage | string,
    data?: TData,
): string => {
    if (!(word in currentWords)) {
        console.warn(`Word "${word}" not defined for locale ${currentLocale}`);
        return word as string;
    }

    return currentWords[word](data);
};

const initialize = () => {
    const INITIAL_LOCALE = supportsLocale(SYSTEM_LOCALE)
        ? SYSTEM_LOCALE
        : DEFAULT_LOCALE;

    setLocale(INITIAL_LOCALE);
};

const languages = {
    en: templatifyLanguage(en),
} as const;

let currentLocale: Locale;
let currentWords: { [name: string]: TemplateExecutor };

const DEFAULT_LOCALE: Locale = "en";

// Intl.DateTimeFormat().resolvedOptions().locale equals something like "en-US"
const SYSTEM_LOCALE = Intl.DateTimeFormat()
    .resolvedOptions()
    .locale.substr(0, 2) as Locale;

initialize();
